/**
 * Cloudflare Turnstile, the "are you a person" check sign-in asks for once
 * attempts start to look like guessing.
 *
 * The API decides when it is needed and says so on the error - either
 * `CAPTCHA_REQUIRED`, or `details.captcha_required` on the failure that made it
 * due - along with the site key and the action to render with. Nothing here
 * keeps its own count.
 *
 * The script is loaded on first use, so a visitor who signs in first time never
 * fetches it.
 */

import { ApiError } from '@/api/client';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface RenderOptions {
  sitekey: string;
  action?: string;
  theme?: 'auto' | 'light' | 'dark';
  size?: 'normal' | 'flexible' | 'compact';
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
}

export interface TurnstileApi {
  render: (container: HTMLElement, options: RenderOptions) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** What the API asked for: render this widget, send its token back. */
export interface CaptchaChallenge {
  siteKey: string;
  action: string;
}

let loading: Promise<TurnstileApi> | null = null;

export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;

  loading = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('The security check did not load.'));
    };
    script.onerror = () => reject(new Error('The security check did not load.'));
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    // Retried on the next render rather than remembered for the page's life.
    loading = null;
    throw error;
  });
  return loading;
}

/** The challenge an API error carries, if it carries one. */
export function captchaChallengeFrom(error: unknown): CaptchaChallenge | null {
  if (!(error instanceof ApiError) || !error.details) return null;
  const { captcha_required: required, captcha_site_key: siteKey, captcha_action: action } =
    error.details;
  if (required !== true || typeof siteKey !== 'string' || typeof action !== 'string') return null;
  return { siteKey, action };
}
